import { formatDistanceToNow } from 'date-fns'
import {
  CheckCircle,
  XCircle,
  Upload,
  AtSign,
  AlertTriangle,
  AlertCircle,
  Bell,
  Trash2,
  Clock,
  Server,
  RefreshCw,
  MessageCircle,
  LifeBuoy,
  WifiOff,
} from 'lucide-react'
import type { UserNotification, NotificationType } from '../types'

interface NotificationItemProps {
  notification: UserNotification
  onMarkAsRead: (id: string) => void
  onDelete: (id: string) => void
  onClick?: (notification: UserNotification) => void
}

const iconMap: Record<NotificationType, typeof CheckCircle> = {
  execution_completed: CheckCircle,
  execution_failed: XCircle,
  workflow_published: Upload,
  comment_mention: AtSign,
  budget_alert: AlertTriangle,
  system_alert: Bell,
  approval_required: Clock,
  approval_decided: CheckCircle,
  cluster_requested: Server,
  engine_down: AlertTriangle,
  engine_restoring: RefreshCw,
  engine_degraded: AlertCircle,
  ticket_reply: MessageCircle,
  ticket_status_changed: LifeBuoy,
  agent_connectivity: WifiOff,
  platform_alert: Bell,
  agent_upgrade_required: AlertTriangle,
}

const iconColorMap: Record<NotificationType, string> = {
  execution_completed: 'text-green-500',
  execution_failed: 'text-red-500',
  workflow_published: 'text-blue-500',
  comment_mention: 'text-purple-500',
  budget_alert: 'text-yellow-500',
  system_alert: 'text-gray-500',
  approval_required: 'text-yellow-500',
  approval_decided: 'text-green-500',
  cluster_requested: 'text-blue-500',
  engine_down: 'text-red-500',
  engine_restoring: 'text-green-500',
  engine_degraded: 'text-yellow-500',
  ticket_reply: 'text-blue-500',
  ticket_status_changed: 'text-primary-500',
  agent_connectivity: 'text-orange-500',
  platform_alert: 'text-gray-500',
  agent_upgrade_required: 'text-amber-500',
}

export function NotificationItem({
  notification,
  onMarkAsRead,
  onDelete,
  onClick,
}: NotificationItemProps) {
  const Icon = iconMap[notification.type] || Bell
  const iconColor = iconColorMap[notification.type] || 'text-gray-500'
  const isUnread = notification.status === 'unread'

  const handleClick = () => {
    if (isUnread) {
      onMarkAsRead(notification.id)
    }
    if (onClick) {
      onClick(notification)
    }
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    onDelete(notification.id)
  }

  const timeAgo = formatDistanceToNow(new Date(notification.created_at), {
    addSuffix: true,
  })

  return (
    <div
      className={`
        group relative flex items-start gap-3 p-3 cursor-pointer
        hover:bg-gray-50 dark:hover:bg-gray-800
        ${isUnread ? 'bg-blue-50/50 dark:bg-blue-900/10' : ''}
      `}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {isUnread && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2">
          <div className="h-2 w-2 rounded-full bg-blue-500" />
        </div>
      )}

      {/* Icon */}
      <div className={`flex-shrink-0 mt-0.5 ${iconColor}`}>
        <Icon className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${isUnread ? 'font-semibold' : 'font-medium'} text-gray-900 dark:text-gray-100`}>
          {notification.title}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2 mt-0.5">
          {notification.message}
        </p>
        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{timeAgo}</p>
      </div>

      {/* Delete button (shows on hover) */}
      <button
        onClick={handleDelete}
        className="
          flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100
          text-gray-400 hover:text-red-500 hover:bg-red-50
          dark:hover:bg-red-900/20 transition-opacity
        "
        title="Delete notification"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
  )
}
